(function () {
  'use strict';

  if (window.UnifiedJourney) {
    return;
  }

  const VERSION = '1.0.0';
  const DEBUG_EVENT_NAME = 'unified:debug';

  const STORAGE_KEYS = {
    journey: 'ut_journey',
    sessionId: 'ut_journey_session_id'
  };

  const MAX_ENTRIES = 200;

  const SENSITIVE_KEYS = [
    'phone_number',
    'email',
    'email_address',
    'message',
    'form_message',
    'first_name',
    'last_name',
    'name',
    'customer_name',
    'customer_email',
    'customer_phone'
  ];

  function getConfig() {
    return window.UnifiedTrackingConfig || {};
  }

  function isDebugEnabled() {
    const config = getConfig();

    return config.debug === true;
  }

  function safeStorageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.sessionStorage.setItem(
        key,
        value
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  function safeStorageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);

      return true;
    } catch (error) {
      return false;
    }
  }

  function getCurrentSessionId() {
    if (
      window.UnifiedContext &&
      typeof window.UnifiedContext
        .getSessionId === 'function'
    ) {
      return window.UnifiedContext
        .getSessionId();
    }

    return '';
  }

  function parseJourney(value) {
    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch (error) {
      return [];
    }
  }

  function loadJourney() {
    const currentSessionId =
      getCurrentSessionId();

    const storedSessionId =
      safeStorageGet(
        STORAGE_KEYS.sessionId
      );

    if (
      currentSessionId &&
      storedSessionId &&
      storedSessionId !== currentSessionId
    ) {
      safeStorageRemove(
        STORAGE_KEYS.journey
      );
    }

    if (currentSessionId) {
      safeStorageSet(
        STORAGE_KEYS.sessionId,
        currentSessionId
      );
    }

    return parseJourney(
      safeStorageGet(
        STORAGE_KEYS.journey
      )
    );
  }

  let journey = loadJourney();

  function saveJourney() {
    safeStorageSet(
      STORAGE_KEYS.journey,
      JSON.stringify(journey)
    );
  }

  function sanitizePrimitive(value) {
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return value;
    }

    return null;
  }

  function sanitizePayload(payload) {
    const source = payload || {};
    const sanitized = {};

    Object.keys(source).forEach(function (key) {
      if (SENSITIVE_KEYS.includes(key)) {
        return;
      }

      const value =
        sanitizePrimitive(source[key]);

      if (value !== null) {
        sanitized[key] = value;
      }
    });

    return sanitized;
  }

  function buildJourneyEntry(
    eventName,
    payload,
    timestamp
  ) {
    const sanitizedPayload =
      sanitizePayload(payload);

    return {
      id: journey.length + 1,

      event: eventName,

      timestamp:
        timestamp ||
        new Date().toISOString(),

      ut_session_id:
        sanitizedPayload.ut_session_id || '',

      page_sequence:
        sanitizedPayload.page_sequence || 0,

      page_path:
        sanitizedPayload.page_path || '',

      page_title:
        sanitizedPayload.page_title || '',

      service:
        sanitizedPayload.service || 'general',

      event_category:
        sanitizedPayload.event_category || '',

      conversion_candidate:
        sanitizedPayload.conversion_candidate === true,

      seconds_since_page_load:
        sanitizedPayload.seconds_since_page_load || 0,

      scroll_depth:
        sanitizedPayload.scroll_depth || 0,

      section_name:
        sanitizedPayload.section_name || '',

      section_index:
        sanitizedPayload.section_index || 0,

      section_location:
        sanitizedPayload.section_location || '',

      click_location:
        sanitizedPayload.click_location || '',

      cta_label:
        sanitizedPayload.cta_label || '',

      email_domain:
        sanitizedPayload.email_domain || '',

      navigation_type:
        sanitizedPayload.navigation_type || '',

      referrer_domain:
        sanitizedPayload.referrer_domain || ''
    };
  }

  function addJourneyEntry(
    eventName,
    payload,
    timestamp
  ) {
    const entry = buildJourneyEntry(
      eventName,
      payload,
      timestamp
    );

    journey.push(entry);

    if (journey.length > MAX_ENTRIES) {
      journey.shift();
    }

    saveJourney();

    if (isDebugEnabled()) {
      console.info(
        '[Unified Journey] Step recorded',
        entry
      );
    }

    return entry;
  }

  function handleDebugEvent(event) {
    const detail = event.detail;

    if (
      !detail ||
      detail.type !== 'event_approved'
    ) {
      return;
    }

    const data = detail.data || {};
    const eventName = data.event;
    const payload = data.payload || {};

    if (!eventName) {
      return;
    }

    addJourneyEntry(
      eventName,
      payload,
      detail.timestamp
    );
  }

  function getJourney() {
    return journey.map(function (entry) {
      return Object.assign({}, entry);
    });
  }

  function printJourney() {
    if (!journey.length) {
      console.info(
        '[Unified Journey] No approved steps recorded.'
      );

      return [];
    }

    console.table(
      journey.map(function (entry) {
        return {
          id: entry.id,
          event: entry.event,
          page_sequence:
            entry.page_sequence,
          page_path:
            entry.page_path,
          service:
            entry.service,
          section:
            entry.section_name,
          location:
            entry.click_location ||
            entry.section_location,
          seconds:
            entry.seconds_since_page_load,
          scroll:
            entry.scroll_depth,
          conversion:
            entry.conversion_candidate
        };
      })
    );

    return getJourney();
  }

  function clearJourney() {
    journey = [];

    safeStorageRemove(
      STORAGE_KEYS.journey
    );

    safeStorageRemove(
      STORAGE_KEYS.sessionId
    );

    console.info(
      '[Unified Journey] Journey cleared.'
    );

    return true;
  }

  function getSummary() {
    const pages = [];
    const services = [];
    const events = {};

    journey.forEach(function (entry) {
      if (
        entry.page_path &&
        !pages.includes(entry.page_path)
      ) {
        pages.push(entry.page_path);
      }

      if (
        entry.service &&
        !services.includes(entry.service)
      ) {
        services.push(entry.service);
      }

      events[entry.event] =
        (events[entry.event] || 0) + 1;
    });

    const conversions =
      journey.filter(function (entry) {
        return (
          entry.conversion_candidate === true
        );
      });

    const lastEntry =
      journey.length
        ? journey[journey.length - 1]
        : null;

    return {
      ut_session_id:
        getCurrentSessionId(),

      total_steps:
        journey.length,

      pages_visited:
        pages.length,

      page_paths:
        pages,

      services_visited:
        services,

      events:
        events,

      conversions:
        conversions.length,

      first_event:
        journey.length
          ? journey[0].event
          : '',

      last_event:
        lastEntry
          ? lastEntry.event
          : '',

      maximum_scroll_depth:
        journey.reduce(
          function (maximum, entry) {
            return Math.max(
              maximum,
              Number(entry.scroll_depth) || 0
            );
          },
          0
        )
    };
  }

  function getConversionPath() {
    const firstConversionIndex =
      journey.findIndex(
        function (entry) {
          return (
            entry.conversion_candidate === true
          );
        }
      );

    if (firstConversionIndex === -1) {
      return [];
    }

    return journey
      .slice(0, firstConversionIndex + 1)
      .map(function (entry) {
        return Object.assign({}, entry);
      });
  }

  function findByEvent(eventName) {
    return journey.filter(function (entry) {
      return entry.event === eventName;
    });
  }

  function findByPage(pagePath) {
    return journey.filter(function (entry) {
      return entry.page_path === pagePath;
    });
  }

  window.addEventListener(
    DEBUG_EVENT_NAME,
    handleDebugEvent
  );

  window.UnifiedJourney = {
    version: VERSION,

    getJourney: getJourney,

    printJourney: printJourney,

    clearJourney: clearJourney,

    getSummary: getSummary,

    getConversionPath:
      getConversionPath,

    findByEvent: findByEvent,

    findByPage: findByPage
  };

  if (isDebugEnabled()) {
    console.info(
      '[Unified Journey] Journey Builder initialized',
      {
        version: VERSION,
        existingSteps: journey.length,
        sessionId: getCurrentSessionId()
      }
    );
  }
})();